import { Mic, People, Settings, Voicemail } from "@mui/icons-material";
import DialpadIcon from "@mui/icons-material/Dialpad";
import HistoryIcon from "@mui/icons-material/History";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useFloatingBubbles } from "../../context/floating-bubbles/FloatingBubblesContext";
import { selectRole } from "../../redux/features/user/selectors/userState.selectors";
import { colors } from "../ui/colors";

import { IRoles } from "src/permissions/types/IRoles";
import { useUnreadMessagesContext } from "../../context/unread-messages/UnreadMessagesContext";
import { useOptionalInternalWebRTC } from "../internal-contacts/_context/InternalWebRTCContext";
import { useOptionalInternalTwilioCall } from "../internal-twilio-call/context/InternalTwilioCallContext";
import { ContactCenterLoadingBody } from "./ContactCenterLoadingBody";
import { ContactCenterShell } from "./ContactCenterShell";
import { VirtualReceptionistContactDetailsHost } from "./contacts/components/VirtualReceptionistContactDetailsHost";
import { VirtualReceptionistLinkPickersHost } from "./contacts/components/VirtualReceptionistLinkPickersHost";
import { useVirtualAssistant } from "./context/VirtualAssistantContext";
import { useSmsContactsQuery } from "./sms/contacts/hooks/useSmsContactsQuery";
import { TabConfigItem, VirtualRecepcionistTypes } from "./types";
import { VirtualReceptionistWidget } from "./VirtualReceptionistWidget";

export const baseColor = colors.orange[50];
export const baseContrastColor = colors.orange[50];
export const contrastColor = "#585757ff";
export const contrastColorHoover = "#000000ff";

const BUSINESS_ROLES = [IRoles.Business, IRoles.Admin, IRoles.Manager, IRoles.VR, IRoles.Other, IRoles.SuperAdmin];

export const tabConfig: TabConfigItem[] = [
  { value: "phone", label: "Dialer", icon: <DialpadIcon sx={{ fontSize: 19 }} />, roles: BUSINESS_ROLES },
  { value: "contacts", label: "Contacts", icon: <People sx={{ fontSize: 19 }} />, roles: [...BUSINESS_ROLES, IRoles.Employee] },
  { value: "history", label: "History", icon: <HistoryIcon sx={{ fontSize: 19 }} />, roles: BUSINESS_ROLES },
  { value: "voice-mail", label: "Voice Mails", icon: <Voicemail sx={{ fontSize: 19 }} />, roles: BUSINESS_ROLES },
  { value: "recording", label: "Recordings", icon: <Mic sx={{ fontSize: 19 }} />, roles: BUSINESS_ROLES },
  { value: "settings", label: "Settings", icon: <Settings sx={{ fontSize: 19 }} />, roles: BUSINESS_ROLES },
];

export const VirtualReceptionistView = () => {
  const userRole = useSelector(selectRole);

  const { view, sessionNumber } = useVirtualAssistant();
  const internalWebRTC = useOptionalInternalWebRTC();
  const internalTwilio = useOptionalInternalTwilioCall();

  const { callsOpen, openCalls, closeCalls, raiseContactCenter, contactCenterZIndex } = useFloatingBubbles();

  const { data: smsContactsPages } = useSmsContactsQuery(sessionNumber);

  const { getTotalUnreadCount } = useUnreadMessagesContext();

  const [type, setType] = useState<VirtualRecepcionistTypes>("phone");

  const showTabs = true;

  const filteredTabConfig = useMemo(() => {
    return tabConfig.filter((tab) => !tab.roles || (userRole && tab.roles.includes(userRole as IRoles)));
  }, [userRole]);

  useEffect(() => {
    if (!showTabs && type !== "phone") setType("phone");
  }, [showTabs, type]);

  useEffect(() => {
    if (!callsOpen) return;

    if (view === "in-call" || view === "incoming") {
      setType("phone");
      return;
    }

    if (internalWebRTC?.internalCallView || internalTwilio?.internalCallView) {
      setType("phone");
    }
  }, [view, callsOpen, internalWebRTC?.internalCallView, internalTwilio?.internalCallView]);

  useEffect(() => {
    if (filteredTabConfig.length > 0 && !filteredTabConfig.some((tab) => tab.value === type)) {
      setType(filteredTabConfig[0].value);
    }
  }, [filteredTabConfig, type]);

  const smsContacts = useMemo(() => {
    return smsContactsPages?.pages?.flatMap((page) => page?.data ?? []) ?? [];
  }, [smsContactsPages]);

  const smsUnreadCount = useMemo(() => {
    return smsContacts.reduce((total, contact) => {
      return total + (contact.unreadCount ?? 0);
    }, 0);
  }, [smsContacts]);

  const internalUnreadCount = getTotalUnreadCount();

  const totalUnreadMessages = smsUnreadCount + internalUnreadCount;

  const hasUnreadMessages = totalUnreadMessages > 0;

  const token = sessionStorage.getItem("token");

  // Keep the shell mounted whenever the user is authenticated. Unmounting the
  // whole widget when role data is briefly unavailable caused it to disappear
  // after company switches or Redux rehydration.
  if (!token) return null;

  const canRenderTabs = Boolean(userRole);

  return (
    <>
      <ContactCenterShell
        expanded={callsOpen}
        totalUnreadMessages={totalUnreadMessages}
        hasUnreadMessages={hasUnreadMessages}
        zIndex={contactCenterZIndex}
        showTabs={showTabs && canRenderTabs}
        type={type}
        filteredTabConfig={filteredTabConfig}
        onExpand={openCalls}
        onCollapse={closeCalls}
        onRaise={raiseContactCenter}
        onTabChange={setType}
      >
        {canRenderTabs ? <VirtualReceptionistWidget type={type} /> : <ContactCenterLoadingBody message="Loading Contact Center..." />}
      </ContactCenterShell>

      {canRenderTabs ? (
        <>
          <VirtualReceptionistContactDetailsHost />
          <VirtualReceptionistLinkPickersHost />
        </>
      ) : null}
    </>
  );
};
